import http from '@bv/utils/http';

/**
 * HTTP methods supported by the API client
 */
type Method = 'get' | 'post' | 'put' | 'patch' | 'delete';

/**
 * Valid response types for HTTP requests
 */
export type ResponseType = 'arraybuffer' | 'blob' | 'document' | 'json' | 'text' | 'stream' | 'formdata';

/**
 * HTTP request params
 */
export type RequestParams = Record<string, any>;

/**
 * HTTP request headers
 */
export type RequestHeaders = Record<string, string | undefined>;

/**
 * Base API response type (union of all possible response shapes)
 * @template D - Type of the response data payload
 */
export type Response<D = any> = SuccessResponse | ListResponse<D> | PaginatedResponse<D> | ItemResponse<D>;

/**
 * Basic success response with message
 * @property {string} message - Success message
 * @property {true} success - Success flag
 */
export type SuccessResponse = {
  data?: string;
  message: string;
  success: true;
};

/**
 * List response containing an array of items
 * @template D - Type of items in the array
 * @extends SuccessResponse
 */
export type ListResponse<D = any> = SuccessResponse & {
  /** Array of items */
  data: D[];
};

/**
 * Single item response
 * @template D - Type of the item
 * @extends SuccessResponse
 */
export type ItemResponse<D = any> = SuccessResponse & {
  /** The response item */
  data: D;
};

/**
 * Paginated list response
 * @template D - Type of items in the array
 * @extends ListResponse<D>
 */
export interface PaginatedResponse<D = any> extends ListResponse<D> {
  /** Pagination metadata */
  meta: PaginationMeta;
}

/**
 * Pagination metadata
 * @property {number} current_page - Current page number
 * @property {number} from - Starting index
 * @property {number} last_page - Last page number
 * @property {number} per_page - Items per page
 * @property {number} to - Ending index
 * @property {number} total - Total items available
 */
export interface PaginationMeta {
  current_page: number;
  from: number;
  last_page: number;
  per_page: number;
  to: number;
  total: number;
}

/**
 * Error response shape
 * @property {Record<string, string[]>} errors - Field validation errors
 * @property {string} message - Error message
 * @property {false} success - Success flag (always false for errors)
 */
export interface ErrorResponse {
  errors: Record<string, string[]>;
  message: string;
  success: false;
}

/**
 * Configuration options for HTTP requests.
 *
 * @property {Record<string, any>} [params] - Query parameters to include in the request URL.
 *                                           These will be serialized as a URL-encoded query string.
 * @property {Record<string, string>} [headers] - Custom HTTP headers to include with the request.
 *                                               These will be merged with any default headers.
 * @property {string} [absoluteURL] - Base URL to override the default endpoint for this request.
 * @property {boolean} [withCredentials] - Indicates whether cross-site requests should include credentials.
 */
export type RequestConfig<D = any> = BaseRequestConfig & {
  method: Method;
  url?: string;
  data?: D;
  absoluteURL?: string;
  withCredentials?: boolean;
  responseType?: ResponseType;
  fullResponse?: boolean;
  signal?: AbortSignal;
};

/**
 * Base request configuration
 * @property {RequestParams} [params] - Query parameters to include in the request URL.
 * @property {RequestHeaders} [headers] - Custom HTTP headers to include with the request.
 * @property {any} [data] - Optional payload data to send with the request.
 */
export interface BaseRequestConfig {
  params?: RequestParams;
  headers?: RequestHeaders;
  data?: any;
}

/**
 * Abstract base class for API services
 * @class BaseApi
 * @abstract
 * @remarks
 * This class serves as a foundation for API service classes.
 * @property {string} endpoint - The base API endpoint URL for all requests.
 *                             Should be overridden by child classes.
 *                             Default: empty string *
 * @example
 * class UserApi extends BaseApi {
 *   protected endpoint: string = 'users';
 * }
 */
export class BaseApi {
  /**
   * Creates an instance of the API client.
   * @param {string} endpoint - The base API endpoint for all requests made by this instance (e.g., 'users', 'products').
   */
  public constructor(public endpoint: string) {}

  /**
   * Make HTTP request
   * @template T - The expected type of the response data.
   * @template R - The expected full response type (default is Response<T>).
   * @template D - The data type (default is any).
   * @param {RequestConfig<D>} [config] - Configuration for the request (params, headers, absoluteURL, etc.).
   * @param {Method} [config.method] - The HTTP method to use (e.g., 'GET', 'POST', 'PUT', etc.).
   * @param {string} [config.url] - The url path (will be prefixed by `config.absoluteURL` or `this.endpoint`).
   * @param {D} [config.data] - Optional payload data to send with the request.
   * @param {Record<string, any>} [config.params] - Query parameters to be sent with the request,
   *                                               will be serialized as URL query string.
   * @param {Record<string, string>} [config.headers] - Custom headers to include with the request,
   *                                                   will be merged with default headers.
   * @param {string} [config.absoluteURL] - Base URL to override the default endpoint,
   *                                   useful for calling external APIs.
   * @param {boolean} [config.withCredentials] - Indicates whether cross-site requests should include credentials.
   * @returns {Promise<R>} A promise that resolves with the full response of type `R`.
   * @throws {ErrorResponse} Throws an error if the request fails.
   * @public
   */
  public async request<T = any, R = Response<T>, D = any>(config: RequestConfig<D>): Promise<R> {
    const { method, url, data, absoluteURL, params, headers, withCredentials, responseType, fullResponse, signal } =
      config;

    try {
      const requestUrl: string = absoluteURL
        ? `${absoluteURL}${url ? `/${url}` : ''}`
        : `${this.endpoint}${url ? `/${url}` : ''}`;
      const response = await http.request<T, R>({
        method,
        url: requestUrl,
        data,
        params,
        headers,
        withCredentials,
        responseType,
        fullResponse,
        signal,
      });
      return response as R;
    } catch (error) {
      throw error as ErrorResponse;
    }
  }
}
